import React, { useState } from 'react';

interface FileItem {
  name: string;
  type: 'file' | 'folder';
  path: string;
  description: string;
  category?: string;
  language?: string;
  size?: string;
  children?: FileItem[];
}

export const ProjectFileExplorer: React.FC = () => {
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set(['/']));
  const [selectedFile, setSelectedFile] = useState<FileItem | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  const projectStructure: FileItem = {
    name: 'cf-fullstack-boilerplate',
    type: 'folder',
    path: '/',
    description: 'CloudFlare Full-Stack Boilerplate Project',
    children: [
      {
        name: 'apps',
        type: 'folder',
        path: '/apps',
        description: 'Application workspaces',
        children: [
          {
            name: 'api',
            type: 'folder',
            path: '/apps/api',
            description: 'CloudFlare Worker API',
            children: [
              {
                name: 'package.json',
                type: 'file',
                path: '/apps/api/package.json',
                description: 'NPM package configuration with Hono.js, AWS SDK, and TypeScript',
                category: 'Configuration',
                language: 'JSON'
              },
              {
                name: 'wrangler.toml',
                type: 'file',
                path: '/apps/api/wrangler.toml',
                description: 'CloudFlare Workers configuration with D1, KV, and R2 bindings',
                category: 'Configuration',
                language: 'TOML'
              },
              {
                name: 'tsconfig.json',
                type: 'file',
                path: '/apps/api/tsconfig.json',
                description: 'TypeScript compiler configuration',
                category: 'Configuration',
                language: 'JSON'
              },
              {
                name: 'schema.sql',
                type: 'file',
                path: '/apps/api/schema.sql',
                description: 'Main database schema with users, folders, files, and share links',
                category: 'Database',
                language: 'SQL'
              },
              {
                name: 'auth-schema.sql',
                type: 'file',
                path: '/apps/api/auth-schema.sql',
                description: 'Authentication schema with password hashing and sessions',
                category: 'Database',
                language: 'SQL'
              },
              {
                name: '.env.example',
                type: 'file',
                path: '/apps/api/.env.example',
                description: 'Environment variables template',
                category: 'Configuration',
                language: 'ENV'
              },
              {
                name: 'src',
                type: 'folder',
                path: '/apps/api/src',
                description: 'Source code directory',
                children: [
                  {
                    name: 'index.ts',
                    type: 'file',
                    path: '/apps/api/src/index.ts',
                    description: 'Main application entry point with route mounting and middleware',
                    category: 'Core',
                    language: 'TypeScript'
                  },
                  {
                    name: 'types.ts',
                    type: 'file',
                    path: '/apps/api/src/types.ts',
                    description: 'TypeScript type definitions for CloudFlare environment',
                    category: 'Core',
                    language: 'TypeScript'
                  },
                  {
                    name: 'auth.ts',
                    type: 'file',
                    path: '/apps/api/src/auth.ts',
                    description: 'JWT authentication system using Web Crypto API',
                    category: 'Auth',
                    language: 'TypeScript'
                  },
                  {
                    name: 'project-structure.ts',
                    type: 'file',
                    path: '/apps/api/src/project-structure.ts',
                    description: 'ASCII art project structure diagrams',
                    category: 'Documentation',
                    language: 'TypeScript'
                  },
                  {
                    name: 'routes',
                    type: 'folder',
                    path: '/apps/api/src/routes',
                    description: 'API route handlers',
                    children: [
                      {
                        name: 'simple-folders.ts',
                        type: 'file',
                        path: '/apps/api/src/routes/simple-folders.ts',
                        description: 'Simple folder management with R2 storage integration',
                        category: 'API',
                        language: 'TypeScript'
                      },
                      {
                        name: 'aws-credentials.ts',
                        type: 'file',
                        path: '/apps/api/src/routes/aws-credentials.ts',
                        description: 'AWS credential storage and management with encryption',
                        category: 'API',
                        language: 'TypeScript'
                      },
                      {
                        name: 'email-send.ts',
                        type: 'file',
                        path: '/apps/api/src/routes/email-send.ts',
                        description: 'Email sending via AWS SES with templates and bulk support',
                        category: 'API',
                        language: 'TypeScript'
                      },
                      {
                        name: 'domain-management.ts',
                        type: 'file',
                        path: '/apps/api/src/routes/domain-management.ts',
                        description: 'Domain verification and DNS management for SES',
                        category: 'API',
                        language: 'TypeScript'
                      },
                      {
                        name: 'email-events.ts',
                        type: 'file',
                        path: '/apps/api/src/routes/email-events.ts',
                        description: 'Email event processing and webhook handling',
                        category: 'API',
                        language: 'TypeScript'
                      },
                      {
                        name: 'otp.ts',
                        type: 'file',
                        path: '/apps/api/src/routes/otp.ts',
                        description: 'One-time password generation and verification',
                        category: 'API',
                        language: 'TypeScript'
                      }
                    ]
                  },
                  {
                    name: 'db',
                    type: 'folder',
                    path: '/apps/api/src/db',
                    description: 'Database schemas',
                    children: [
                      {
                        name: 'schema.sql',
                        type: 'file',
                        path: '/apps/api/src/db/schema.sql',
                        description: 'Core database schema with users and sessions',
                        category: 'Database',
                        language: 'SQL'
                      },
                      {
                        name: 'email-events-schema.sql',
                        type: 'file',
                        path: '/apps/api/src/db/email-events-schema.sql',
                        description: 'Email lifecycle tracking with events and statistics',
                        category: 'Database',
                        language: 'SQL'
                      },
                      {
                        name: 'aws-credentials-schema.sql',
                        type: 'file',
                        path: '/apps/api/src/db/aws-credentials-schema.sql',
                        description: 'Encrypted AWS credential storage schema',
                        category: 'Database',
                        language: 'SQL'
                      },
                      {
                        name: 'domain-management-schema.sql',
                        type: 'file',
                        path: '/apps/api/src/db/domain-management-schema.sql',
                        description: 'Domain verification tracking schema',
                        category: 'Database',
                        language: 'SQL'
                      },
                      {
                        name: 'otp-schema.sql',
                        type: 'file',
                        path: '/apps/api/src/db/otp-schema.sql',
                        description: 'One-time password system schema',
                        category: 'Database',
                        language: 'SQL'
                      }
                    ]
                  }
                ]
              },
              {
                name: 'docs',
                type: 'folder',
                path: '/apps/api/docs',
                description: 'Documentation',
                children: [
                  {
                    name: 'SNS-SETUP-GUIDE.md',
                    type: 'file',
                    path: '/apps/api/docs/SNS-SETUP-GUIDE.md',
                    description: 'Complete AWS SNS setup guide for SES event handling',
                    category: 'Documentation',
                    language: 'Markdown'
                  }
                ]
              }
            ]
          },
          {
            name: 'web',
            type: 'folder',
            path: '/apps/web',
            description: 'React frontend application',
            children: [
              {
                name: 'src',
                type: 'folder',
                path: '/apps/web/src',
                description: 'React source code',
                children: [
                  {
                    name: 'App.tsx',
                    type: 'file',
                    path: '/apps/web/src/App.tsx',
                    description: 'Main React app with routing configuration',
                    category: 'Core',
                    language: 'TypeScript React'
                  },
                  {
                    name: 'components',
                    type: 'folder',
                    path: '/apps/web/src/components',
                    description: 'React components',
                    children: [
                      {
                        name: 'FolderCreationSteps.tsx',
                        type: 'file',
                        path: '/apps/web/src/components/FolderCreationSteps.tsx',
                        description: 'Step-by-step visualization of folder creation process',
                        category: 'Component',
                        language: 'TypeScript React'
                      },
                      {
                        name: 'LinksDirectory.tsx',
                        type: 'file',
                        path: '/apps/web/src/components/LinksDirectory.tsx',
                        description: 'Comprehensive directory of all system URLs',
                        category: 'Component',
                        language: 'TypeScript React'
                      },
                      {
                        name: 'IAMPermissionPalace.tsx',
                        type: 'file',
                        path: '/apps/web/src/components/IAMPermissionPalace.tsx',
                        description: 'AWS IAM user creation wizard with CSV import',
                        category: 'Component',
                        language: 'TypeScript React'
                      },
                      {
                        name: 'EmailSender.tsx',
                        type: 'file',
                        path: '/apps/web/src/components/EmailSender.tsx',
                        description: 'Email composition and sending interface',
                        category: 'Component',
                        language: 'TypeScript React'
                      },
                      {
                        name: 'DomainManager.tsx',
                        type: 'file',
                        path: '/apps/web/src/components/DomainManager.tsx',
                        description: 'Domain verification and DNS record management',
                        category: 'Component',
                        language: 'TypeScript React'
                      },
                      {
                        name: 'ClientFolderManager.tsx',
                        type: 'file',
                        path: '/apps/web/src/components/ClientFolderManager.tsx',
                        description: 'File upload and folder management interface',
                        category: 'Component',
                        language: 'TypeScript React'
                      },
                      {
                        name: 'ShareAccessPortal.tsx',
                        type: 'file',
                        path: '/apps/web/src/components/ShareAccessPortal.tsx',
                        description: 'Recipient portal for accessing shared files',
                        category: 'Component',
                        language: 'TypeScript React'
                      },
                      {
                        name: 'SESQuest.tsx',
                        type: 'file',
                        path: '/apps/web/src/components/SESQuest.tsx',
                        description: 'Gamified AWS SES setup tutorial',
                        category: 'Component',
                        language: 'TypeScript React'
                      },
                      {
                        name: 'SESQuestReal.tsx',
                        type: 'file',
                        path: '/apps/web/src/components/SESQuestReal.tsx',
                        description: 'Real AWS SES setup with credential storage',
                        category: 'Component',
                        language: 'TypeScript React'
                      }
                    ]
                  }
                ]
              }
            ]
          }
        ]
      }
    ]
  };

  const toggleFolder = (path: string) => {
    const newExpanded = new Set(expandedFolders);
    if (newExpanded.has(path)) {
      newExpanded.delete(path);
    } else {
      newExpanded.add(path);
    }
    setExpandedFolders(newExpanded);
  };

  const getFileIcon = (item: FileItem) => {
    if (item.type === 'folder') {
      return expandedFolders.has(item.path) ? '📂' : '📁';
    }

    switch (item.language) {
      case 'TypeScript': return '📘';
      case 'TypeScript React': return '⚛️';
      case 'SQL': return '🗃️';
      case 'JSON': return '📋';
      case 'Markdown': return '📝';
      case 'TOML': return '⚙️';
      case 'ENV': return '🔐';
      default: return '📄';
    }
  };

  const getCategoryColor = (category?: string) => {
    switch (category) {
      case 'Core': return 'text-purple-400';
      case 'API': return 'text-green-400';
      case 'Database': return 'text-blue-400';
      case 'Configuration': return 'text-yellow-400';
      case 'Component': return 'text-pink-400';
      case 'Documentation': return 'text-orange-400';
      case 'Auth': return 'text-red-400';
      default: return 'text-gray-400';
    }
  };

  const renderFileTree = (item: FileItem, depth: number = 0) => {
    const isExpanded = expandedFolders.has(item.path);
    const isSelected = selectedFile?.path === item.path;
    const matchesSearch = searchTerm === '' ||
      item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.description.toLowerCase().includes(searchTerm.toLowerCase());

    if (searchTerm && !matchesSearch && item.type === 'file') {
      return null;
    }

    return (
      <div key={item.path}>
        <div
          className={`flex items-center gap-2 py-1 px-2 hover:bg-gray-800 rounded cursor-pointer ${
            isSelected ? 'bg-gray-800 border-l-2 border-blue-500' : ''
          }`}
          style={{ paddingLeft: `${depth * 20 + 8}px` }}
          onClick={() => {
            if (item.type === 'folder') {
              toggleFolder(item.path);
            } else {
              setSelectedFile(item);
            }
          }}
        >
          <span className="text-lg">{getFileIcon(item)}</span>
          <span className={`flex-1 text-sm ${item.type === 'folder' ? 'font-bold' : ''}`}>
            {item.name}
          </span>
          {item.category && (
            <span className={`text-xs ${getCategoryColor(item.category)}`}>
              {item.category}
            </span>
          )}
        </div>
        {item.type === 'folder' && isExpanded && item.children && (
          <div>
            {item.children.map(child => renderFileTree(child, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  const countFiles = (item: FileItem): { files: number; folders: number } => {
    if (item.type === 'file') {
      return { files: 1, folders: 0 };
    }

    let count = { files: 0, folders: 1 };
    if (item.children) {
      for (const child of item.children) {
        const childCount = countFiles(child);
        count.files += childCount.files;
        count.folders += childCount.folders;
      }
    }
    return count;
  };

  const stats = countFiles(projectStructure);

  return (
    <div className="min-h-screen bg-gray-900 text-white p-8">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">📂 Project File Explorer</h1>
          <p className="text-gray-400">Complete documentation of every file in the CloudFlare fullstack boilerplate</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          <div className="bg-gray-800 rounded-lg p-4">
            <p className="text-2xl font-bold">{stats.files}</p>
            <p className="text-sm text-gray-400">Total Files</p>
          </div>
          <div className="bg-gray-800 rounded-lg p-4">
            <p className="text-2xl font-bold">{stats.folders}</p>
            <p className="text-sm text-gray-400">Total Folders</p>
          </div>
          <div className="bg-gray-800 rounded-lg p-4">
            <p className="text-2xl font-bold">TypeScript</p>
            <p className="text-sm text-gray-400">Primary Language</p>
          </div>
          <div className="bg-gray-800 rounded-lg p-4">
            <p className="text-2xl font-bold">Monorepo</p>
            <p className="text-sm text-gray-400">Architecture</p>
          </div>
        </div>

        {/* Search */}
        <div className="mb-6">
          <input
            type="text"
            placeholder="Search files..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-4 py-2 bg-gray-800 rounded border border-gray-700 text-white"
          />
        </div>

        <div className="grid grid-cols-2 gap-6">
          {/* File Tree */}
          <div className="bg-gray-800 rounded-lg p-4 max-h-[600px] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">File Structure</h2>
            {renderFileTree(projectStructure)}
          </div>

          {/* File Details */}
          <div className="bg-gray-800 rounded-lg p-4">
            <h2 className="text-xl font-bold mb-4">File Details</h2>
            {selectedFile ? (
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-gray-400">Name</p>
                  <p className="font-bold">{selectedFile.name}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-400">Path</p>
                  <code className="text-xs text-blue-400">{selectedFile.path}</code>
                </div>
                <div>
                  <p className="text-sm text-gray-400">Type</p>
                  <p>{selectedFile.type === 'folder' ? '📁 Folder' : '📄 File'}</p>
                </div>
                {selectedFile.language && (
                  <div>
                    <p className="text-sm text-gray-400">Language</p>
                    <p>{selectedFile.language}</p>
                  </div>
                )}
                {selectedFile.category && (
                  <div>
                    <p className="text-sm text-gray-400">Category</p>
                    <p className={getCategoryColor(selectedFile.category)}>{selectedFile.category}</p>
                  </div>
                )}
                <div>
                  <p className="text-sm text-gray-400">Description</p>
                  <p className="text-sm">{selectedFile.description}</p>
                </div>
              </div>
            ) : (
              <p className="text-gray-400">Select a file or folder to view details</p>
            )}
          </div>
        </div>

        {/* Technology Stack */}
        <div className="mt-8 bg-gray-800 rounded-lg p-6">
          <h2 className="text-xl font-bold mb-4">🛠️ Technology Stack</h2>
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div>
              <h3 className="font-bold mb-2 text-blue-400">Runtime & Framework</h3>
              <ul className="space-y-1">
                <li>• CloudFlare Workers</li>
                <li>• Hono.js (Express-like)</li>
                <li>• React with Vite</li>
                <li>• TypeScript</li>
              </ul>
            </div>
            <div>
              <h3 className="font-bold mb-2 text-green-400">Storage & Database</h3>
              <ul className="space-y-1">
                <li>• CloudFlare D1 (SQLite)</li>
                <li>• CloudFlare R2 (S3-compatible)</li>
                <li>• CloudFlare KV Store</li>
                <li>• Local SQLite for dev</li>
              </ul>
            </div>
            <div>
              <h3 className="font-bold mb-2 text-purple-400">Services & Tools</h3>
              <ul className="space-y-1">
                <li>• AWS SES for email</li>
                <li>• AWS SNS for webhooks</li>
                <li>• JWT authentication</li>
                <li>• Turborepo monorepo</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Key Features */}
        <div className="mt-8 bg-gray-800 rounded-lg p-6">
          <h2 className="text-xl font-bold mb-4">✨ Key Features</h2>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <span className="text-green-500">✓</span>
                <span>Complete AWS SES email integration</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-green-500">✓</span>
                <span>R2 file storage with folder management</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-green-500">✓</span>
                <span>Domain verification and DNS management</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-green-500">✓</span>
                <span>Real-time event processing with webhooks</span>
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <span className="text-green-500">✓</span>
                <span>Secure credential storage with encryption</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-green-500">✓</span>
                <span>JWT authentication system</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-green-500">✓</span>
                <span>Step-by-step process visualization</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-green-500">✓</span>
                <span>Comprehensive audit logging</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};